import { ApiResult, SearchDTO, SearchVM } from 'common/models';
import { HttpClient } from 'common/services/http-client.service';

import { PagedRequest } from 'common/models/data/pagedRequest.model';
import previewDataConfig from './previewData.config';
import { CategoryDTO, EntityDetails } from 'common/models/data/entity.model';
import { IErrorApiResponse } from 'common/models/api-service/error-response.model';
import { CategoryErrorVM, PreviewCategoriesVM } from 'common/models/data/previewData.model';
import { PaginatedApiResponse } from 'common/models/api-service/api-response.model';
import { IndicatorUpdateDTO } from 'common/models/data/indicator.model';
import { EntityType } from 'common/enums';

export class PreviewDataService extends HttpClient {
    public static async getCategories(
        pagedRequest: PagedRequest
    ): Promise<
        ApiResult<PaginatedApiResponse<PreviewCategoriesVM> | IErrorApiResponse> | undefined
    > {
        return PreviewDataService.invokeApi<
            PaginatedApiResponse<PreviewCategoriesVM> | IErrorApiResponse
        >(previewDataConfig.getCategoriesUrl(pagedRequest));
    }

    public static async getAllEntities(
        pagedRequest: PagedRequest
    ): Promise<
        ApiResult<PaginatedApiResponse<PreviewCategoriesVM> | IErrorApiResponse> | undefined
    > {
        return PreviewDataService.invokeApi<
            PaginatedApiResponse<PreviewCategoriesVM> | IErrorApiResponse
        >(previewDataConfig.getAllEntitiesUrl(pagedRequest));
    }

    public static async getEntities(
        categoryId: string
    ): Promise<ApiResult<PreviewCategoriesVM | IErrorApiResponse> | undefined> {
        return PreviewDataService.invokeApi<PreviewCategoriesVM | IErrorApiResponse>(
            previewDataConfig.getEntitiesUrl(categoryId)
        );
    }

    public static async getErrorCount(): Promise<
        ApiResult<PreviewCategoriesVM | IErrorApiResponse> | undefined
    > {
        return PreviewDataService.invokeApi<PreviewCategoriesVM | IErrorApiResponse>(
            previewDataConfig.getErrorCountUrl()
        );
    }

    public static async getIndicator(
        indicatorId: string
    ): Promise<ApiResult<PreviewCategoriesVM | IErrorApiResponse> | undefined> {
        return PreviewDataService.invokeApi<PreviewCategoriesVM | IErrorApiResponse>(
            previewDataConfig.getIndicatorUrl(indicatorId)
        );
    }

    public static async deleteIndicator(
        indicatorId: string
    ): Promise<ApiResult<PreviewCategoriesVM | IErrorApiResponse> | undefined> {
        return PreviewDataService.invokeApi<PreviewCategoriesVM | IErrorApiResponse>(
            previewDataConfig.deleteIndicatorUrl(indicatorId)
        );
    }

    public static async updateIndicator(
        indicatorId: string,
        data: IndicatorUpdateDTO
    ): Promise<ApiResult<PreviewCategoriesVM | IErrorApiResponse> | undefined> {
        return PreviewDataService.invokeApi<PreviewCategoriesVM | IErrorApiResponse>(
            previewDataConfig.updateIndicatorUrl(indicatorId, data)
        );
    }

    public static async getCategoriesErrorDetails(
        pagedRequest: PagedRequest
    ): Promise<ApiResult<PaginatedApiResponse<CategoryErrorVM> | IErrorApiResponse> | undefined> {
        return PreviewDataService.invokeApi(previewDataConfig.getCategoriesErrorUrl(pagedRequest));
    }

    public static async getCategoriesErrorDetailsByEntity(
        categoryId: string
    ): Promise<ApiResult<CategoryErrorVM[]> | IErrorApiResponse | undefined> {
        return PreviewDataService.invokeApi(
            previewDataConfig.getCategoriesErrorByEntityUrl(categoryId)
        );
    }

    public static async getErrorDetails(
        categoryId: string
    ): Promise<ApiResult<PreviewCategoriesVM | IErrorApiResponse> | undefined> {
        return PreviewDataService.invokeApi<PreviewCategoriesVM | IErrorApiResponse>(
            previewDataConfig.getErrorDetailsUrl(categoryId)
        );
    }

    public static async deleteCategory(
        categoryId: string
    ): Promise<ApiResult<PreviewCategoriesVM | IErrorApiResponse> | undefined> {
        return PreviewDataService.invokeApi<PreviewCategoriesVM | IErrorApiResponse>(
            previewDataConfig.deleteCategoryUrl(categoryId)
        );
    }

    public static async updateCategory(
        categoryId: string,
        data: CategoryDTO
    ): Promise<ApiResult<CategoryDTO | IErrorApiResponse> | undefined> {
        return PreviewDataService.invokeApi<CategoryDTO | IErrorApiResponse>(
            previewDataConfig.updateCategoryUrl(categoryId, data)
        );
    }

    public static async getEntityDetails(
        categoryId: string,
        entityType: EntityType
    ): Promise<ApiResult<EntityDetails> | undefined> {
        return PreviewDataService.invokeApi(
            previewDataConfig.getEntityDetailsUrl(categoryId, entityType)
        );
    }

    public static async getSearchList(
        data: SearchDTO
    ): Promise<ApiResult<PaginatedApiResponse<SearchVM[]>> | undefined> {
        return PreviewDataService.invokeApi<PaginatedApiResponse<SearchVM[]>>(
            previewDataConfig.getSearchUrl(data)
        );
    }

    public static async getEntityPath(
        categoryId: string
    ): Promise<ApiResult<PreviewCategoriesVM | IErrorApiResponse> | undefined> {
        return PreviewDataService.invokeApi<PreviewCategoriesVM | IErrorApiResponse>(
            previewDataConfig.getEntityPathUrl(categoryId)
        );
    }
}
